/**
 * User projects data access, shared by both apps.
 */
import { apiFetch } from './api'

export interface Project {
  id: string
  user_id: string
  name: string
  description: string | null
  polygon_geojson: GeoJSON.Polygon
  area_ha: number | null
  center_lat: number | null
  center_lng: number | null
  status: 'active' | 'archived'
  created_at: string
  updated_at: string
}

export interface CreateProjectInput {
  name: string
  description?: string
  polygon: GeoJSON.Polygon
  area_ha?: number
  center_lat?: number
  center_lng?: number
}

export const projectsService = {
  /**
   * Create a new project
   */
  async createProject(input: CreateProjectInput): Promise<{ project: Project | null; error: Error | null }> {
    const { data, error } = await apiFetch<Project>('/user-projects', {
      method: 'POST',
      body: JSON.stringify({
        name: input.name,
        description: input.description || null,
        polygon: input.polygon,
        area_ha: input.area_ha,
        center_lat: input.center_lat,
        center_lng: input.center_lng,
      }),
    })
    return { project: data, error }
  },

  /**
   * Get all projects for current user
   */
  async getProjects(): Promise<{ projects: Project[]; error: Error | null }> {
    const { data, error } = await apiFetch<{ total: number; projects: Project[] }>('/user-projects')
    return { projects: data?.projects || [], error }
  },

  /**
   * Get a single project by ID
   */
  async getProject(projectId: string): Promise<{ project: Project | null; error: Error | null }> {
    const { data, error } = await apiFetch<Project>(`/user-projects/${projectId}`)
    return { project: data, error }
  },

  /**
   * Update a project
   */
  async updateProject(
    projectId: string,
    updates: Partial<Pick<Project, 'name' | 'description' | 'status'>>
  ): Promise<{ project: Project | null; error: Error | null }> {
    const { data, error } = await apiFetch<Project>(`/user-projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    })
    return { project: data, error }
  },

  /**
   * Archive a project
   */
  async archiveProject(projectId: string): Promise<{ error: Error | null }> {
    const { error } = await apiFetch(`/user-projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'archived' }),
    })
    return { error }
  },

  /**
   * Delete a project
   */
  async deleteProject(projectId: string): Promise<{ error: Error | null }> {
    const { error } = await apiFetch(`/user-projects/${projectId}`, {
      method: 'DELETE',
    })
    return { error }
  },
}
